import { BASE_URL } from '@ai-platform/utils';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { ScreenSize, useGetScreenSize } from '../../hooks/useGetScreenSize';
import { parseCategoriesFromUrl, buildCategoriesPath } from './HomePage.helpers';
import { UseCategoriesFromUrlResult } from './HomePage.types';

/**
 * Хук для определения количества колонок в ленте промптов
 * в зависимости от размера экрана
 *
 * Соответствует логике CSS Grid из Feed.scss:
 * - mobile (до 374px): 1 колонка
 * - mobile (375px - 1023px): 2 колонки
 * - tablet (1024px - 1365px): 3 колонки
 * - desktop (1366px - 1919px): 4 колонки
 * - large-desktop (1920px+): 5 колонок
 *
 * @returns {PromptFeedColumns} Количество колонок для текущего размера экрана
 */
export const usePromptFeedColumns = (): number => {
    const screenSize = useGetScreenSize();

    switch (screenSize) {
        case ScreenSize.Mobile:
            return 2;
        case ScreenSize.Tablet:
            return 3;
        case ScreenSize.SmallDesktop:
            return 3;
        case ScreenSize.Desktop:
            return 4;
        case ScreenSize.MiddleDesktop:
            return 4;
        case ScreenSize.LargeDesktop:
            return 5;
        default:
            return 2; // fallback для неопределенных случаев
    }
};

/**
 * Хук для двусторонней синхронизации выбранных категорий с URL
 * Поддерживает схему:
 * - / → пустой массив
 * - /c/category → ['category']
 * - /c/category?categories=cat1_cat2 → ['category', 'cat1', 'cat2']
 *
 * @returns {UseCategoriesFromUrlResult} Объект с категориями и функцией обновления
 */
export const useSelectedCategoriesFromUrl = (): UseCategoriesFromUrlResult => {
    const { category } = useParams<{ category?: string }>();
    const [searchParams] = useSearchParams();
    // parseCategoriesFromUrl(category, searchParams.get('categories') || undefined)
    // Мемоизируем парсинг категорий для избежания лишних вычислений
    const parsedCategories = useMemo(() => {
        const queryCategories = searchParams.get('categories') || undefined;

        return parseCategoriesFromUrl(category, queryCategories);
    }, [category, searchParams]);
    const [selectedCategories, setSelectedCategories] = useState<string[]>(parsedCategories);

    // Синхронизация состояния с URL при изменении URL
    useEffect(() => {
        setSelectedCategories(parsedCategories);
    }, [parsedCategories]);

    // Функция для обновления категорий с синхронизацией URL
    const updateSelectedCategories = useCallback((categories: string[]) => {
        const newPath = buildCategoriesPath(categories);

        // Используем window.history.replaceState для обновления URL без сброса скролла
        if (typeof window !== 'undefined') {
            window.history.replaceState(null, '', BASE_URL + newPath);
        }

        // Обновляем состояние напрямую, чтобы избежать лишнего useEffect
        setSelectedCategories(categories);
    }, []);

    // Мемоизируем результат для избежания создания нового объекта при каждом рендере
    return useMemo(
        () => ({
            selectedCategories,
            updateSelectedCategories,
        }),
        [selectedCategories, updateSelectedCategories],
    );
};
